import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { LanguageSwitcher } from "./LanguageSwitcher";
import globalMarketLogo from "@/assets/global-market-logo-color.webp";

interface NavItem {
  label: string;
  href: string;
  isHome?: boolean;
}

interface MobileMenuProps {
  navItems: NavItem[];
  scrollToSection: (href: string, isHome?: boolean) => void;
  onClose: () => void;
}

function MobileMenu({ navItems, scrollToSection, onClose }: MobileMenuProps) {
  const { t } = useTranslation();
  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 bg-black/50 z-[60] md:hidden"
        onClick={onClose}
      />

      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 25, stiffness: 200 }}
        className="fixed top-0 right-0 bottom-0 w-[85%] max-w-sm bg-background z-[70] md:hidden"
      >
        <div className="flex items-center justify-between p-5 border-b border-border">
          <img 
            src={globalMarketLogo} 
            alt="MH Global Market" 
            width={1024}
            height={768}
            className="h-9 w-auto"
          />
          <button
            onClick={onClose}
            className="p-2 rounded hover:bg-muted transition-colors"
            aria-label="Close menu"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="flex flex-col p-5">
          {navItems.map((item, index) => (
            <motion.button
              key={item.href}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              onClick={() => scrollToSection(item.href, item.isHome)}
              className="flex items-center justify-between py-4 px-2 text-lg font-medium text-foreground hover:text-primary border-b border-border/50 transition-colors group"
            >
              <span>{item.label}</span>
              <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
            </motion.button>
          ))}
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-border bg-muted/30">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              {t("nav.language", "Sprache")}
            </span>
            <LanguageSwitcher />
          </div>
        </div>
      </motion.div>
    </>
  );
}

export function LandingNav() {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  const navItems: NavItem[] = [
    { label: t("nav.home"), href: "#top", isHome: true },
    { label: t("nav.approach"), href: "#ansatz" },
    { label: t("nav.services"), href: "#leistungen" },
    { label: t("nav.international"), href: "#international" },
    { label: t("nav.faq"), href: "#faq" },
  ];

  const scrollToSection = (href: string, isHome?: boolean) => {
    if (isHome) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
    setMobileMenuOpen(false);
  };

  // Only show nav when scrolled past hero
  if (!isScrolled) {
    return (
      <>
        {/* Mobile Menu Button - Always visible on mobile */}
        <div className="fixed top-6 right-6 z-50 md:hidden flex items-center gap-3">
          <LanguageSwitcher variant="light" />
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 transition-colors"
            aria-label="Toggle menu"
          >
            <Menu className="h-6 w-6 text-white" />
          </button>
        </div>

        {/* Desktop - Only Language Switcher when not scrolled */}
        <div className="fixed top-6 right-6 z-50 hidden md:block">
          <LanguageSwitcher variant="light" />
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <MobileMenu
              navItems={navItems}
              scrollToSection={scrollToSection}
              onClose={() => setMobileMenuOpen(false)}
            />
          )}
        </AnimatePresence>
      </>
    );
  }

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/98 backdrop-blur-md border-b border-border/50 transform-gpu">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6 lg:gap-8">
              {navItems.map((item) => (
                <button
                  key={item.href}
                  onClick={() => scrollToSection(item.href, item.isHome)}
                  className={`text-sm transition-colors text-muted-foreground hover:text-foreground ${
                    item.isHome ? "font-medium" : ""
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            {/* Language Switcher & Mobile Menu Toggle */}
            <div className="flex items-center gap-3 ml-auto">
              <LanguageSwitcher variant="dark" />
              
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded hover:bg-muted transition-colors"
                aria-label="Toggle menu"
              >
                <Menu className="h-6 w-6 text-foreground" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Canvas Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <MobileMenu
            navItems={navItems}
            scrollToSection={scrollToSection}
            onClose={() => setMobileMenuOpen(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
}
